import { Paper, Stack, Typography } from '@mui/material'
import React from 'react'

import Icon from 'components/Icon'
import { ExternalActionDescription } from 'components/scenarios/common/ExternalActionDescription'
import { GRAPH_NODE_PAPER_PROPS } from 'components/scenarios/concurrent/concurrent-scenario-form-utils'
import { GraphNodeGlyphIcon } from 'components/scenarios/concurrent/GraphNodeGlyphIcon'
import { muiTheme } from 'mui-theme'
import {
  type ExternalActionNode,
  ExternalActionRequestType,
  type GraphNode,
  type RollbackExternalActionNode
} from 'types/api/scenarios/scenario'

export const ExternalActionGraphNode: React.FC<{
  node: ExternalActionNode | RollbackExternalActionNode
  isClickable?: boolean
  rowActions?: React.ReactNode
  onClick?: (node: GraphNode) => void
}> = ({ node, isClickable = false, rowActions, onClick }) => {
  /*
   * Failed reason takes priority over warning message.
   * We only want to display one on the node itself; both will be displayed in the details drawer.
   */
  const actionStateMessage =
    node.externalActionRequest.type === ExternalActionRequestType.NoOp
      ? 'Skipped - no target available'
      : node.state.failed_reason ?? node.state.warning_message
  const actionStateMessageColor = node.state.failed_reason
    ? muiTheme.palette.error.main
    : muiTheme.palette.warning.main

  return (
    <Paper
      data-testid={`external-action-graph-node-${node.guid}`}
      sx={[
        GRAPH_NODE_PAPER_PROPS,
        !rowActions && { paddingRight: 2 },
        (muiTheme) => ({
          marginY: muiTheme.spacing(2)
        })
      ]}
      {...(isClickable && onClick && { onClick: () => onClick(node) })}
    >
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between'
        }}
      >
        <Stack direction="row" sx={{ alignItems: 'center' }}>
          <GraphNodeGlyphIcon node={node} />
          <Stack sx={{ maxWidth: '100%', rowGap: 0.5 }}>
            <ExternalActionDescription node={node} />
            {actionStateMessage && (
              <Stack
                direction="row"
                sx={{ alignItems: 'top', columnGap: 0.5, flexWrap: 'nowrap' }}
              >
                <Icon
                  css={{
                    color: actionStateMessageColor
                  }}
                  icon="solidWarning"
                />
                <Typography color={actionStateMessageColor} variant="caption">
                  {actionStateMessage}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>

        {rowActions && (
          <Stack
            sx={{
              justifyContent: 'center',
              alignContent: 'center'
            }}
          >
            {rowActions}
          </Stack>
        )}
      </Stack>
    </Paper>
  )
}
